import { useState, useEffect, useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Trophy, MapPin } from 'lucide-react';
import { sportsService, SportConfiguration } from '@/services/sportsService';
import { getSportVariantDisplayText, getMinPlayersText } from '@/config/sportsConfig';

interface SportVariantSelectorProps {
  selectedSportId?: number;
  selectedVariantId?: string;
  selectedGender?: string;
  onSportChange: (sportId: number) => void;
  onVariantChange: (variant: SportConfiguration | null) => void;
  onGenderChange?: (gender: string) => void;
}

export const SportVariantSelector = ({
  selectedSportId,
  selectedVariantId,
  onSportChange,
  onVariantChange
}: SportVariantSelectorProps) => {
  const [sportsWithVariants, setSportsWithVariants] = useState<any[]>([]);
  const [variants, setVariants] = useState<SportConfiguration[]>([]);
  const [selectedVariant, setSelectedVariant] = useState<SportConfiguration | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSportsWithVariants();
  }, []);

  useEffect(() => {
    if (selectedSportId) {
      loadVariantsForSport(selectedSportId);
    } else {
      setVariants([]);
      setSelectedVariant(null);
      onVariantChange(null);
    }
  }, [selectedSportId]);

  useEffect(() => {
    if (selectedVariantId && variants.length > 0) {
      const variant = variants.find(v => v.id === selectedVariantId);
      setSelectedVariant(variant || null);
      onVariantChange(variant || null);
    }
  }, [selectedVariantId, variants]);

  const loadSportsWithVariants = async () => {
    try {
      const data = await sportsService.getSportsWithVariants();
      setSportsWithVariants(data);
    } catch (error) {
      console.error('Error loading sports:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadVariantsForSport = async (sportId: number) => {
    try {
      const configurations = await sportsService.getSportConfigurations(sportId);
      setVariants(configurations);
      
      // Auto-select default variant if no variant is currently selected
      if (!selectedVariantId) {
        const defaultVariant = configurations.find(v => v.is_default_variant) || configurations[0];
        if (defaultVariant) {
          setSelectedVariant(defaultVariant);
          onVariantChange(defaultVariant);
        }
      }
    } catch (error) {
      console.error('Error loading variants:', error);
    }
  };

  const handleSportChange = (sportId: string) => {
    const id = parseInt(sportId);
    onSportChange(id);
    setSelectedVariant(null);
  };

  const handleVariantChange = (variantId: string) => {
    const variant = variants.find(v => v.id === variantId);
    setSelectedVariant(variant || null);
    onVariantChange(variant || null);
  };

  // Deduplicate variants by display_format + variant_name (gender is now separate)
  const uniqueVariants = useMemo(() => {
    const seen = new Set<string>();
    return variants.filter((v) => {
      const key = `${v.display_format}|${v.variant_name}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [variants]);

  if (loading) {
    return <div className="space-y-4">Loading sports...</div>;
  }

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="sport_select">Sport *</Label>
        <Select 
          value={selectedSportId?.toString() || ''} 
          onValueChange={handleSportChange}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a sport" />
          </SelectTrigger>
          <SelectContent className="bg-background border z-[9999] max-h-60 overflow-y-auto">
            {sportsWithVariants.map((sport) => (
              <SelectItem key={sport.id} value={sport.id.toString()}>
                {sport.sport_name} ({sport.variants.length} variant{sport.variants.length !== 1 ? 's' : ''})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {uniqueVariants.length > 0 && (
        <div>
          <Label htmlFor="variant_select">Game Format *</Label>
          <Select 
            value={selectedVariant?.id || ''} 
            onValueChange={handleVariantChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select game format" />
            </SelectTrigger>
            <SelectContent className="bg-background border z-[9999] max-h-60 overflow-y-auto">
              {uniqueVariants.map((variant) => (
                <SelectItem key={variant.id} value={variant.id}>
                  {variant.display_format} — {variant.variant_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {selectedVariant && (
        <Card className="bg-muted/50">
          <CardContent className="pt-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">{getSportVariantDisplayText(selectedVariant)}</h4>
                <Badge>{selectedVariant.display_format}</Badge>
              </div>
              
              <p className="text-sm text-muted-foreground">
                {selectedVariant.variant_description}
              </p>
              
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="font-medium">{selectedVariant.min_players_per_team}</div>
                    <div className="text-muted-foreground">Min per team</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="font-medium">{selectedVariant.total_teams}</div>
                    <div className="text-muted-foreground">Teams</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="font-medium text-xs">{selectedVariant.field_size}</div>
                    <div className="text-muted-foreground">Field Size</div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};